import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';
import { T, HAIRLINE, SHADOW } from './siteUI';

/* =========================================================
   Sample call player.

   We sell voice, so a real recording argues better than a video. This plays
   one sample call inline.

   If the file is missing or unplayable the component renders NOTHING rather
   than a dead control — the audio lives in public/audio/ and is not in the
   repo, so a site deployed without it must simply not show a player.
   ========================================================= */

const fmt = (secs) => {
  if (!Number.isFinite(secs)) return '0:00';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

/* Static bar heights — a suggestion of a waveform, not a real analysis of the
   file. Fixed so it does not shimmer randomly on every render. */
const BARS = [7, 12, 18, 11, 22, 15, 26, 19, 30, 21, 16, 24, 13, 20, 10, 17, 23, 14, 9, 15];

const SampleCall = ({ src, label, caption, className = '' }) => {
  const audioRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    // Reset when the source changes (e.g. navigating between solutions).
    setReady(false);
    setFailed(false);
    setPlaying(false);
    setTime(0);
    setDuration(0);
  }, [src]);

  /* Nothing to show until the browser confirms it can actually play this. */
  if (failed) return null;

  const audio = () => audioRef.current;

  const toggle = () => {
    const el = audio();
    if (!el) return;
    if (el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => setFailed(true));
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  const seek = (e) => {
    const el = audio();
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    el.currentTime = ratio * duration;
    setTime(el.currentTime);
  };

  const pct = duration ? (time / duration) * 100 : 0;

  return (
    <div
      className={`inline-flex items-center gap-3.5 rounded-2xl bg-white px-4 py-3 ${HAIRLINE} ${SHADOW.card} ${
        ready ? '' : 'opacity-0'
      } ${className}`}
    >
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onCanPlay={() => setReady(true)}
        onError={() => setFailed(true)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onEnded={() => {
          setPlaying(false);
          setTime(0);
        }}
      />

      <button
        type="button"
        onClick={toggle}
        aria-label={playing ? `Pause ${label}` : `Play ${label}`}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-teal-600 text-white transition-colors hover:bg-teal-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
      >
        {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" className="ml-0.5" />}
      </button>

      <div className="min-w-0">
        <p className="text-[12.5px] font-semibold leading-tight">{label}</p>
        {caption && <p className={`mt-0.5 ${T.micro}`}>{caption}</p>}

        {/* Seekable progress, drawn as bars so it reads as audio rather than
            as a generic loading bar. */}
        <div
          role="slider"
          tabIndex={0}
          aria-label={`Seek within ${label}`}
          aria-valuemin={0}
          aria-valuemax={Math.round(duration) || 0}
          aria-valuenow={Math.round(time)}
          aria-valuetext={`${fmt(time)} of ${fmt(duration)}`}
          onClick={seek}
          onKeyDown={(e) => {
            const el = audio();
            if (!el) return;
            if (e.key === 'ArrowRight') el.currentTime = Math.min(duration, el.currentTime + 5);
            if (e.key === 'ArrowLeft') el.currentTime = Math.max(0, el.currentTime - 5);
            if (e.key === ' ' || e.key === 'Enter') {
              e.preventDefault();
              toggle();
            }
          }}
          className="mt-2 flex h-6 w-[168px] cursor-pointer items-end gap-[3px] rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600"
        >
          {BARS.map((h, i) => {
            const played = (i / BARS.length) * 100 < pct;
            return (
              <span
                key={i}
                className={`w-[4px] rounded-full transition-colors ${played ? 'bg-teal-500' : 'bg-slate-200'}`}
                style={{ height: `${h}px` }}
              />
            );
          })}
        </div>
      </div>

      <span className={`shrink-0 self-end tabular-nums ${T.micro}`}>
        {fmt(time)} / {fmt(duration)}
      </span>
    </div>
  );
};

export default SampleCall;
